/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : SettingsResetWarningModal.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Account selection popup
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : Mar 01, 2022
 *
 * @Version      : Version 1.0
 *
 * By            : <Author>
 * On            : <Date>
 * Description   : <Change Description>
 *
 *
 ******************************************************************************/

import React, { FC, useState, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Grid } from "@scuf/common";
import {
  setCurrentWorkFlowStep,
  selectCurrentWorkFlowStep,
  selectCurrentApmFileRecord,
} from "stores/ApmWorkFlowStore";
import {
  setSelectedApmFile,
  selectApmFileNames,
} from "stores/SelectApmFileStore";
import {
  SaveAircraftDetails,
  setSelectedAircraftDetails,
} from "stores/AircraftDetailsStore";
import {
  setSettingsResetWarning,
  selectSettingsResetWarning,
} from "stores/UIStore";

import { EnumNavigateAction } from "models/configuration";

export const SettingsResetWarningModal: FC = () => {
  const settingsResetWarning = useSelector(selectSettingsResetWarning);
  const currentWorkFlowStep = useSelector(selectCurrentWorkFlowStep);
  const currentApmFileRecord = useSelector(selectCurrentApmFileRecord);
  const apmFileNames = useSelector(selectApmFileNames);

  const [modalOpen, setModalOpen] = useState(true);
  const dispatch = useDispatch();

  const closeModal = () => {
    setModalOpen(false);
    dispatch(
      setSettingsResetWarning({
        showSettingsResetWarningModal: false,
        navigateActionData: null,
        navigateAction: null,
      })
    );
  };

  const onModalCloseHandler = () => {
    if (currentWorkFlowStep === 2) {
      if (currentApmFileRecord?.baseFileName !== null) {
        const selectedApmFile = apmFileNames?.find(
          (apmFileName) =>
            apmFileName.partNumber === currentApmFileRecord?.baseFileName
        );

        if (selectedApmFile === undefined) {
          dispatch(setSelectedApmFile(null));
        } else {
          dispatch(setSelectedApmFile(selectedApmFile));
        }
      } else {
        dispatch(setSelectedApmFile(null));
      }
    } else if (currentWorkFlowStep === 3) {
      if (currentApmFileRecord) {
        const previousAircraftDetails = {
          make: currentApmFileRecord?.make,
          model: currentApmFileRecord?.model,
          tailNumber: currentApmFileRecord?.tailNumber,
          serialNumber: currentApmFileRecord?.serialNumber,
        };

        dispatch(setSelectedAircraftDetails(previousAircraftDetails));
      }
    }
    closeModal();
  };

  const onContinueClickHandler = () => {
    if (currentWorkFlowStep === 2) {
      if (settingsResetWarning.navigateAction === EnumNavigateAction.next) {
        dispatch(setCurrentWorkFlowStep(3));
        closeModal();
      } else if (
        settingsResetWarning.navigateAction === EnumNavigateAction.skip
      ) {
        dispatch(setSelectedApmFile(null));
        dispatch(setCurrentWorkFlowStep(3));
        closeModal();
      }
    } else if (currentWorkFlowStep === 3) {
      if (
        settingsResetWarning.navigateAction === EnumNavigateAction.save ||
        settingsResetWarning.navigateAction === EnumNavigateAction.next
      ) {
        if (settingsResetWarning.navigateActionData) {
          dispatch(
            SaveAircraftDetails(
              settingsResetWarning.navigateActionData,
              settingsResetWarning.navigateAction
            )
          );
        }
        closeModal();
      }
    }
  };

  const getModalContent = (): ReactElement => {
    return (
      <p>
        Changing some of the existing values may reset the APM settings entered
        previously. Do you want to continue?
      </p>
    );
  };

  return (
    <Modal
      className="customer-change-warning-modal"
      size="large"
      closeIcon={true}
      closeOnDimmerClick={false}
      open={modalOpen}
      onClose={onModalCloseHandler}
    >
      <Modal.Header>Confirm</Modal.Header>
      <Modal.Content scrolling={false} className="modal-content">
        <Grid>
          <Grid.Row>
            <Grid.Column>{getModalContent()}</Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Footer>
        <Grid>
          <Grid.Row>
            <Grid.Column className="account-modal-footer-button-container">
              <Button
                type="secondary"
                size="medium"
                className="cancel-customer-popup-button"
                content="Cancel"
                onClick={onModalCloseHandler}
              />
              <Button
                type="primary"
                size="medium"
                content="Continue"
                className="select-customer-popup-button"
                onClick={onContinueClickHandler}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Footer>
    </Modal>
  );
};
